import React, { useEffect, useState } from 'react';
import apiService from '../../services/api';
import { formatARS } from '../../utils/formatCurrency';
import { ColorKey } from '../../utils/pricingConfig';
import { receiptColorStyle, COLOR_LABELS, bagColorStyle } from '../../utils/warehouseStock';
import AttachmentDropzone from './AttachmentDropzone';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';
import { SupplierPriceData, suggestSupplierPrice } from '../../utils/supplierPrices';

interface DetailLine {
  anchoCm: number;
  largoCm: number;
  color: ColorKey;
  micronaje: number;
  unidad: 'bolsas' | 'packs';
  cantidad: number;
  bolsasPorPack: number;
  precioUnitario: number;
  precioFigurativo: boolean;
  precioFigurativoNota: string;
  itemNote: string;
  bolsasRecibidas?: number;
  bolsasCobradas?: number;
  // Where the unit price came from: the supplier's list, the last remito, or typed by hand.
  priceSource?: 'lista' | 'ultimo' | 'manual';
  priceDate?: string;
}

const today = () => new Date().toLocaleDateString('sv');

// Un "pack" es siempre PACK_SIZE bolsas (igual que en el stock).
const PACK_SIZE = 50;

const emptyDetailLine = (): DetailLine => ({
  anchoCm: 45, largoCm: 60, color: 'negro', micronaje: 23, unidad: 'bolsas', cantidad: 0, bolsasPorPack: PACK_SIZE, precioUnitario: 0, precioFigurativo: false, precioFigurativoNota: '', itemNote: ''
});

const lineTotalBolsas = (line: DetailLine) => line.unidad === 'packs' ? line.cantidad * PACK_SIZE : line.cantidad;
const receivedBags = (line: DetailLine) => line.bolsasRecibidas ?? lineTotalBolsas(line);
const billedBags = (line: DetailLine) => line.bolsasCobradas ?? lineTotalBolsas(line);
// Importe "de verdad" de la linea (lo que muestra el campo "Importe de la linea").
const lineAmount = (line: DetailLine) => billedBags(line) * (line.precioUnitario || 0);
// Lo que suma al monto total del remito: si el precio es solo de referencia, no cuenta.
const lineSubtotal = (line: DetailLine) => line.precioFigurativo ? 0 : lineAmount(line);

const describeDetailLine = (line: DetailLine): string => {
  if (!line.cantidad) return '';
  const colorLabel = COLOR_LABELS[line.color];
  const micronesSuffix = line.micronaje ? ` ${line.micronaje} micrones` : '';
  const base = line.unidad === 'packs'
    ? `${line.cantidad} Packs x ${PACK_SIZE}u (${lineTotalBolsas(line)} bolsas) ${line.anchoCm}x${line.largoCm}cm ${colorLabel}${micronesSuffix}`
    : `${line.cantidad} Bolsas ${line.anchoCm}x${line.largoCm}cm ${colorLabel}${micronesSuffix}`;
  if (line.precioUnitario <= 0) return base;
  if (line.precioFigurativo) {
    const motivo = line.precioFigurativoNota ? `: ${line.precioFigurativoNota}` : '';
    return `${base} · ${formatARS(line.precioUnitario)} c/u (precio de referencia, no suma al total${motivo})`;
  }
  return `${base} · ${formatARS(line.precioUnitario)} c/u = ${formatARS(lineAmount(line))}`;
};

interface EditingReceipt {
  id: number;
  receiptNumber: string;
  receiptDate: string;
  amount: number;
  note: string;
  attachmentPath: string;
  items: Array<{ ancho: number; largo: number; color: string; microns: number; unidad: 'bolsas' | 'packs'; cantidad: number; bolsasPorPack: number | null; bolsasRecibidas?:number; bolsasCobradas?:number; unitPrice: number | null; priceIsFigurative?: boolean; priceFigurativeNote?: string | null; itemNote: string | null; loadStock?: boolean }>;
}

interface Props {
  supplierId: number;
  editingReceipt?: EditingReceipt | null;
  onClose: () => void;
  onSaved: () => void;
}

const linesFromReceipt = (receipt: EditingReceipt): DetailLine[] => receipt.items.map(item => ({
  anchoCm: item.ancho, largoCm: item.largo, color: item.color as ColorKey, micronaje: item.microns,
  unidad: item.unidad, cantidad: item.cantidad, bolsasPorPack: PACK_SIZE, precioUnitario: item.unitPrice || 0,
  precioFigurativo: item.priceIsFigurative || false,
  precioFigurativoNota: item.priceFigurativeNote || '',
  itemNote: item.itemNote || '',
  bolsasRecibidas: item.bolsasRecibidas ?? (item.loadStock === false ? 0 : undefined),
  bolsasCobradas: item.bolsasCobradas,
  priceSource: item.unitPrice ? 'manual' : undefined,
}));

const formatDate = (iso?: string) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '');

// Fills the price from the supplier's list (or the last price paid) unless it was typed by hand.
const withSuggestedPrice = (line: DetailLine, data: SupplierPriceData | null): DetailLine => {
  if (line.priceSource === 'manual' || !data) return line;
  const suggested = suggestSupplierPrice(data, { ancho: line.anchoCm, largo: line.largoCm, color: line.color, microns: line.micronaje });
  if (!suggested) return line.priceSource ? { ...line, precioUnitario: 0, priceSource: undefined, priceDate: undefined } : line;
  return { ...line, precioUnitario: suggested.unitPrice, priceSource: suggested.source, priceDate: suggested.receiptDate };
};

const SupplierReceiptModal: React.FC<Props> = ({ supplierId, editingReceipt, onClose, onSaved }) => {
  useBodyScrollLock(true);
  useEscapeToClose(true, onClose);
  const [receiptNumber, setReceiptNumber] = useState(editingReceipt?.receiptNumber || '');
  const [receiptDate, setReceiptDate] = useState(editingReceipt?.receiptDate || today());
  const [amount, setAmount] = useState(editingReceipt?.amount || 0);
  // El monto se calcula solo desde las líneas mientras el usuario no lo escriba a mano.
  const [amountManual, setAmountManual] = useState(Boolean(editingReceipt && (editingReceipt.amount || 0) > 0));
  const [note, setNote] = useState(editingReceipt?.note || '');
  const [attachmentPath, setAttachmentPath] = useState(editingReceipt?.attachmentPath || '');
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState<DetailLine[]>(editingReceipt ? linesFromReceipt(editingReceipt) : [emptyDetailLine()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [priceData, setPriceData] = useState<SupplierPriceData | null>(null);

  useEffect(() => {
    let active = true;
    apiService.getSupplierPrices(supplierId)
      .then((data) => {
        if (!active) return;
        setPriceData(data);
        setItems((current) => current.map((line) => withSuggestedPrice(line, data)));
      })
      .catch(() => { /* sin lista: el precio se carga a mano */ });
    return () => { active = false; };
  }, [supplierId]);

  const computedTotal = Number(items.reduce((sum, line) => sum + lineSubtotal(line), 0).toFixed(2));

  useEffect(() => {
    if (!amountManual) setAmount(computedTotal);
  }, [computedTotal, amountManual]);

  const updateItem = (index: number, patch: Partial<DetailLine>) => setItems(current => current.map((line, i) => {
    if (i !== index) return line;
    const next = { ...line, ...patch };
    if ('precioUnitario' in patch) return { ...next, priceSource: 'manual', priceDate: undefined };
    const articleChanged = ['anchoCm', 'largoCm', 'color', 'micronaje'].some((key) => key in patch);
    return articleChanged ? withSuggestedPrice(next, priceData) : next;
  }));
  const addItem = () => setItems(current => [...current, withSuggestedPrice(emptyDetailLine(), priceData)]);

  // Articles to pick from: the supplier's list, then those bought before that are not in it (last price paid).
  const listedPrices = priceData?.prices || [];
  const boughtNotListed = (priceData?.lastPaid || []).filter(item => !suggestSupplierPrice({ prices: listedPrices, lastPaid: [] }, item));
  const pickArticle = (index: number, value: string) => {
    const [kind, position] = value.split(':');
    const entry = kind === 'lista' ? listedPrices[Number(position)] : kind === 'ultimo' ? boughtNotListed[Number(position)] : null;
    if (!entry) return;
    setItems(current => current.map((line, i) => i !== index ? line : {
      ...line, anchoCm: Number(entry.ancho), largoCm: Number(entry.largo), color: entry.color as ColorKey, micronaje: Number(entry.microns || 0),
      precioUnitario: entry.unitPrice, priceSource: kind as 'lista' | 'ultimo', priceDate: 'receiptDate' in entry ? entry.receiptDate : undefined,
    }));
  };
  const selectedArticle = (line: DetailLine) => {
    const same = (entry: { ancho: number; largo: number; color: string; microns: number }) => Number(entry.ancho) === line.anchoCm && Number(entry.largo) === line.largoCm && entry.color === line.color && Number(entry.microns || 0) === line.micronaje;
    const listed = listedPrices.findIndex(same);
    if (listed >= 0) return `lista:${listed}`;
    const bought = boughtNotListed.findIndex(same);
    return bought >= 0 ? `ultimo:${bought}` : '';
  };
  const removeItem = (index: number) => setItems(current => current.length > 1 ? current.filter((_, i) => i !== index) : current);

  const handleAttachmentChange = async (file: File | undefined) => {
    if (!file) return;
    try {
      setUploading(true);
      setError('');
      const response = await apiService.uploadAccountAttachment(file);
      setAttachmentPath(response.path);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo subir el adjunto.');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    const validItems = items.filter(line => describeDetailLine(line));
    if (validItems.length === 0) { setError('Agregá al menos una línea de detalle.'); return; }
    try {
      setSaving(true);
      setError('');
      const payload = {
        receiptDate,
        receiptNumber: receiptNumber || undefined,
        amount,
        note: note || undefined,
        attachmentPath: attachmentPath || undefined,
        items: validItems.map(line => ({
          ancho: line.anchoCm, largo: line.largoCm, color: line.color, microns: line.micronaje,
          unidad: line.unidad, cantidad: line.cantidad, bolsasPorPack: line.unidad === 'packs' ? PACK_SIZE : undefined,
          bolsasRecibidas: receivedBags(line), bolsasCobradas: billedBags(line),
          unitPrice: line.precioUnitario > 0 ? line.precioUnitario : undefined,
          priceIsFigurative: line.precioUnitario > 0 ? line.precioFigurativo : undefined,
          priceFigurativeNote: line.precioUnitario > 0 && line.precioFigurativo ? (line.precioFigurativoNota || undefined) : undefined,
          itemNote: line.itemNote || undefined,
        })),
      };
      if (editingReceipt) {
        await apiService.updateSupplierReceipt(editingReceipt.id, payload);
      } else {
        await apiService.createSupplierReceipt(supplierId, payload);
      }
      onSaved();
      onClose();
      if (amount <= 0) {
        window.alert('Remito guardado. El stock ya se actualizó en "Stock propio". Como no cargaste un monto, todavía no aparece en la cuenta corriente: vas a verlo listado en "Remitos de proveedor cargados" con un botón para cargar el monto cuando lo tengas.');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo guardar el remito.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto rounded-t-xl sm:rounded-xl bg-white shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">{editingReceipt ? 'Editar remito de proveedor' : 'Remito de proveedor'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">×</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-6 py-5">
          <div><label className="text-sm font-medium">N° de remito</label><input className="w-full border rounded px-3 py-2" value={receiptNumber} onChange={e => setReceiptNumber(e.target.value)} /></div>
          <div><label className="text-sm font-medium">Fecha</label><input type="date" className="w-full border rounded px-3 py-2" value={receiptDate} max={today()} onChange={e => setReceiptDate(e.target.value)} /></div>
          <div>
            <label className="text-sm font-medium">Monto total sin IVA (ARS)</label>
            <input type="number" min={0} className="w-full border rounded px-3 py-2" value={amount}
              onChange={e => { setAmountManual(true); setAmount(Number(e.target.value)); }} />
            {!amountManual && computedTotal > 0 && (
              <p className="mt-1 text-xs text-green-700">Calculado automáticamente por las líneas (precio unitario × bolsas).</p>
            )}
            {amountManual && computedTotal > 0 && amount !== computedTotal && (
              <p className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs font-medium text-amber-800">⚠ No coincide con la suma de líneas ({formatARS(computedTotal)}).
                <button type="button" onClick={() => setAmountManual(false)} className="ml-1 text-primary-700 hover:text-primary-800 font-semibold underline">Usar ese monto</button>
              </p>
            )}
            {computedTotal <= 0 && (
              <p className="mt-1 text-xs text-gray-500">Cargá el precio por bolsa sin IVA en cada línea y se calcula solo. O dejalo en 0 y agregás el monto después con "Nuevo movimiento".</p>
            )}
          </div>
        </div>

        <div className="px-6">
          <label className="text-sm font-medium">Detalle (mercaderia recibida)</label>
          <div className="space-y-3 mt-1">
            {items.map((line, index) => (
              <div key={index} className="border rounded-lg p-3 space-y-2 bg-gray-50">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center rounded px-2 py-1 text-xs font-semibold" style={receiptColorStyle(COLOR_LABELS[line.color])}>
                    {line.anchoCm || '?'} × {line.largoCm || '?'} · {COLOR_LABELS[line.color]}{line.micronaje ? ` · ${line.micronaje}μ` : ''}
                  </span>
                  <button type="button" onClick={() => removeItem(index)} disabled={items.length <= 1} className="px-2 py-1 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-40 text-xs">Quitar linea</button>
                </div>

                {(listedPrices.length > 0 || boughtNotListed.length > 0) && (() => {
                  // Articles already on another line of this remito are not offered again.
                  const taken = new Set(items.filter((_, i) => i !== index).map(selectedArticle).filter(Boolean));
                  return (
                  <select value={selectedArticle(line)} onChange={e => pickArticle(index, e.target.value)} className="w-full rounded-md border border-blue-200 bg-blue-50/50 px-2 py-1.5 text-sm text-blue-900">
                    <option value="">Elegir de la lista del proveedor...</option>
                    {listedPrices.length > 0 && <optgroup label="Lista de precios">
                      {listedPrices.map((price, i) => taken.has(`lista:${i}`) ? null : <option key={price.id} value={`lista:${i}`}>{price.ancho} x {price.largo} cm · {COLOR_LABELS[price.color as ColorKey] || price.color}{price.microns ? ` · ${price.microns}μ` : ''} · {formatARS(price.unitPrice)} c/u</option>)}
                    </optgroup>}
                    {boughtNotListed.length > 0 && <optgroup label="Comprados antes (último precio pagado)">
                      {boughtNotListed.map((item, i) => taken.has(`ultimo:${i}`) ? null : <option key={`u${i}`} value={`ultimo:${i}`}>{item.ancho} x {item.largo} cm · {COLOR_LABELS[item.color as ColorKey] || item.color}{item.microns ? ` · ${item.microns}μ` : ''} · {formatARS(item.unitPrice)} c/u</option>)}
                    </optgroup>}
                  </select>
                  );
                })()}
                {priceData && !listedPrices.length && !boughtNotListed.length && <p className="text-xs text-amber-700">Este proveedor no tiene lista de precios. Podés cargarla en su pestaña "Lista de precios".</p>}

                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                  <div><label className="text-xs text-gray-600">Ancho (cm)</label><input type="number" min={0} className="w-full border rounded px-2 py-1.5 text-sm" value={line.anchoCm} onChange={e => updateItem(index, { anchoCm: Number(e.target.value) })} /></div>
                  <div><label className="text-xs text-gray-600">Largo (cm)</label><input type="number" min={0} className="w-full border rounded px-2 py-1.5 text-sm" value={line.largoCm} onChange={e => updateItem(index, { largoCm: Number(e.target.value) })} /></div>
                  <div>
                    <label className="text-xs text-gray-600">Color</label>
                    <div className="relative">
                      <span
                        className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full"
                        style={{ backgroundColor: bagColorStyle(COLOR_LABELS[line.color]).fill as string, boxShadow: line.color === 'blanco' ? 'inset 0 0 0 1px #d1d5db' : undefined }}
                      />
                      <select className="w-full rounded border py-1.5 pl-6 pr-2 text-sm" value={line.color} onChange={e => updateItem(index, { color: e.target.value as ColorKey })}>
                        {(Object.keys(COLOR_LABELS) as ColorKey[]).map(c => <option key={c} value={c}>{COLOR_LABELS[c]}</option>)}
                      </select>
                    </div>
                  </div>
                  <div><label className="text-xs text-gray-600">Micronaje</label><input type="number" min={0} className="w-full border rounded px-2 py-1.5 text-sm" value={line.micronaje} onChange={e => updateItem(index, { micronaje: Number(e.target.value) })} /></div>
                  <div><label className="text-xs text-gray-600">Unidad</label><select className="w-full border rounded px-2 py-1.5 text-sm" value={line.unidad} onChange={e => updateItem(index, { unidad: e.target.value as 'bolsas' | 'packs' })}><option value="bolsas">Bolsas</option><option value="packs">Packs</option></select></div>
                  <div><label className="text-xs text-gray-600">Cantidad</label><input type="number" min={0} className="w-full border rounded px-2 py-1.5 text-sm" value={line.cantidad} onChange={e => updateItem(index, { cantidad: Number(e.target.value) })} /></div>
                  {line.unidad === 'packs' && <div><label className="text-xs text-gray-600">Bolsas por pack</label><input type="number" className="w-full border rounded px-2 py-1.5 text-sm bg-gray-100 text-gray-500" value={PACK_SIZE} disabled readOnly /></div>}
                  <div>
                    <label className="text-xs text-gray-600">Precio por bolsa sin IVA (ARS)</label>
                    <input type="number" min={0} className="w-full border rounded px-2 py-1.5 text-sm" value={line.precioUnitario} onChange={e => updateItem(index, { precioUnitario: Number(e.target.value) })} />
                    {(() => {
                      if (line.priceSource === 'lista') return <p className="mt-0.5 text-[11px] text-green-700">De la lista del proveedor</p>;
                      if (line.priceSource === 'ultimo') return <p className="mt-0.5 text-[11px] text-amber-700">Último precio pagado ({formatDate(line.priceDate)}), no está en la lista</p>;
                      const suggested = suggestSupplierPrice(priceData, { ancho: line.anchoCm, largo: line.largoCm, color: line.color, microns: line.micronaje });
                      if (line.priceSource === 'manual' && suggested && suggested.unitPrice !== line.precioUnitario) {
                        return <p className="mt-0.5 text-[11px] text-gray-500">{suggested.source === 'lista' ? 'Lista' : 'Último pagado'}: {formatARS(suggested.unitPrice)} <button type="button" onClick={() => setItems(current => current.map((l, i) => i === index ? { ...l, priceSource: undefined } : l).map((l, i) => i === index ? withSuggestedPrice(l, priceData) : l))} className="font-semibold text-primary-700 underline">usar</button></p>;
                      }
                      return null;
                    })()}
                  </div>
                  {line.precioUnitario > 0 && <div><label className="text-xs text-gray-600">Importe de la línea</label><p className="w-full rounded px-2 py-1.5 text-sm font-medium text-gray-700">{formatARS(lineAmount(line))}</p></div>}
                </div>

                {line.precioUnitario > 0 && (
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={line.precioFigurativo} onChange={e => updateItem(index, { precioFigurativo: e.target.checked })} />
                      Precio solo de referencia (no suma al monto total ni a la cuenta corriente)
                    </label>
                    {line.precioFigurativo && (
                      <input
                        value={line.precioFigurativoNota}
                        onChange={e => updateItem(index, { precioFigurativoNota: e.target.value })}
                        placeholder="Explicá por qué este precio es solo de referencia (ej: precio viejo, cortesía, canje, a confirmar)"
                        className="w-full border rounded px-2 py-1.5 text-sm"
                      />
                    )}
                  </div>
                )}

                <div><label className="text-xs text-gray-600">Nota de esta línea (opcional)</label><input value={line.itemNote} onChange={e => updateItem(index, { itemNote: e.target.value })} placeholder="Ej: faltante del remito anterior" className="w-full border rounded px-2 py-1.5 text-sm" /></div>

                <div className="grid grid-cols-2 gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <div><label className="text-xs font-medium text-blue-900">Bolsas recibidas</label><input type="number" min={0} value={receivedBags(line)} onChange={e=>updateItem(index,{bolsasRecibidas:Number(e.target.value)})} className="mt-1 w-full rounded border px-2 py-1.5 text-sm"/><p className="mt-1 text-[11px] text-blue-700">Solo esta cantidad entra al stock.</p></div>
                  <div><label className="text-xs font-medium text-blue-900">Bolsas cobradas</label><input type="number" min={0} value={billedBags(line)} onChange={e=>updateItem(index,{bolsasCobradas:Number(e.target.value)})} className="mt-1 w-full rounded border px-2 py-1.5 text-sm"/><p className="mt-1 text-[11px] text-blue-700">Esta cantidad calcula el importe.</p></div>
                  {receivedBags(line)!==billedBags(line)&&<p className="col-span-2 text-xs font-medium text-amber-800">Quedará una diferencia abierta de {Math.abs(receivedBags(line)-billedBags(line)).toLocaleString('es-AR')} bolsas.</p>}
                </div>

                {describeDetailLine(line) && <p className="text-xs text-gray-500 italic">{describeDetailLine(line)}</p>}
              </div>
            ))}
            <button type="button" onClick={addItem} className="text-sm text-primary-600 hover:text-primary-700 font-medium">+ Agregar linea</button>
          </div>
          {computedTotal > 0 && (
            <p className="mt-2 text-sm text-gray-600">
              Total por líneas: <span className="font-semibold">{formatARS(computedTotal)}</span>
              {amountManual && amount !== computedTotal && <span className="ml-2 text-amber-700">(el monto total cargado es {formatARS(amount)})</span>}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 px-6 py-5">
          <label className="block text-sm font-medium text-gray-700 col-span-2">Nota<input value={note} onChange={e => setNote(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2" /></label>
          <div className="col-span-2">
            <AttachmentDropzone value={attachmentPath} uploading={uploading} onFileSelected={handleAttachmentChange} />
          </div>
        </div>

        {error && <div className="mx-6 mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <button onClick={onClose} className="rounded-lg border px-4 py-2">Cancelar</button>
          <button onClick={submit} disabled={saving || uploading} className="rounded-lg bg-primary-600 px-4 py-2 text-white disabled:opacity-50">{saving ? 'Guardando...' : editingReceipt ? 'Guardar cambios' : 'Guardar remito'}</button>
        </div>
      </div>
    </div>
  );
};

export default SupplierReceiptModal;
